import { h, Fragment } from 'preact';
import I18n from './I18n';
import { getCompanyProductType, getJewelleryPriceBreakUpConfig } from './Amalgam';
import { getFormattedPrice } from './locale';
import { ENTITY_BASE } from './Utils';

const formatStoneDetailsTitle = ({ entityType, entityKeyDetails }) => {
  if (!entityKeyDetails) {
    return '';
  }
  const entityTypeValue = entityType !== 'DIAMOND' ? entityType : undefined;
  const { color, clarity, shape } = entityKeyDetails;
  return [[entityTypeValue, color, clarity].filter((value) => value).join('-'), shape]
    .filter((value) => value)
    .join(' ');
};

const formatStoneDetailsDescription = ({
  entityKeyDetails,
  hideWeight = false,
  hideRate = false
}) => {
  if (!entityKeyDetails) {
    return '';
  }
  const { pieces, carat, ratePerCt } = entityKeyDetails;
  return [
    !hideWeight
      ? `${carat ? `${carat} ct` : ''}  ${
        pieces ? `(${pieces} pc${pieces === 1 ? '' : 's'})` : ''
      } `
      : '',
    !hideRate && ratePerCt ? `${getFormattedPrice(ratePerCt)}/ct` : ''
  ]
    .filter((value) => value)
    .join(' x ');
};

const formatStoneDiscount = (entityKeyDetails) => {
  const { discount } = entityKeyDetails || {};

  if (!discount) {
    return '';
  }

  return I18n.t('TEN_PERCENT_OFF').replace('10%', discount);
};

export const getJewelleryTypeProductsCharges = ({ productTypeDetails, productTypeRateCharges }) => {
  if (
    getCompanyProductType() !== 'JEWELLERY' ||
    !Array.isArray(productTypeRateCharges) ||
    !productTypeRateCharges.length
  ) {
    return null;
  }

  const metalCharges = [];
  const diamondCharges = [];
  const stoneCharges = [];
  const makingCharges = [];
  const wastageCharges = [];
  const otherCharges = [];

  const jewelleryPriceBreakUpConfig = getJewelleryPriceBreakUpConfig() || {};
  const {
    hideMetalGrossWeightPerGram = false,
    hideMetalNetWeightPerGram = false,
    hideMetalRatePerGram = false,
    hideMakingChargesSection = false,
    hideMakingChargesWeight = false,
    hideMakingChargesRate = false,
    hideDiamondStoneWeight = false,
    hideDiamondStoneRate = false,
    hideWastageChargesSection = false,
    hideMetalSummarySection = false,
    hideStoneSummarySection = false
  } = jewelleryPriceBreakUpConfig;

  productTypeRateCharges.forEach((productTypeRateCharge) => {
    const { id, entityBase, rate, formattedRate, entityType } = productTypeRateCharge;
    const charge = { id, entityBase, rate, formattedRate, entityType };
    const gm = I18n.t('GM');
    const gms = I18n.t('GMS');

    const formatWeight = (weight) => `${weight} ${weight === 1 ? gm : gms}`;

    switch (entityBase) {
      case ENTITY_BASE.METAL: {
        charge.entityCalculation = productTypeRateCharge.entityCalculation;
        const { grams, perGramCost } = productTypeRateCharge.entityCalculation || {};
        const { grossWeight = (productTypeDetails || {}).grossWeight } =
          productTypeRateCharge.entityDetails || {};
        const { quality = (productTypeDetails || {}).entityQuality, units = 'kt' } =
          productTypeRateCharge.entityDetails || {};
        if (!hideMetalSummarySection) {
          metalCharges.push({
            ...charge,
            title: `${quality}${units} ${entityType}`,
            description: () => (
              <Fragment>
                {!hideMetalGrossWeightPerGram && grossWeight ? (
                  <div className="description-detail">
                    <span className="title">{I18n.t('GROSS_WT')}</span>
                    <span className="separator">:</span>
                    <span className="value">{formatWeight(grossWeight)}</span>
                  </div>
                ) : null}
                {!hideMetalNetWeightPerGram && grams ? (
                  <div className="description-detail">
                    <span className="title">{I18n.t('NET_WT')}</span>
                    <span className="separator">:</span>
                    <span className="value">{formatWeight(grams)}</span>
                  </div>
                ) : null}
                {!hideMetalRatePerGram && perGramCost ? (
                  <div className="description-detail">
                    <span className="title">{'Rate' || I18n.t('RATE')}</span>
                    <span className="separator">:</span>
                    <span className="value">{`${getFormattedPrice(perGramCost)}/${gm}`}</span>
                  </div>
                ) : null}
              </Fragment>
            )
          });
        }
        return;
      }

      case ENTITY_BASE.STONE: {
        charge.entityKeyDetails = productTypeRateCharge.entityKeyDetails;
        charge.title = formatStoneDetailsTitle(productTypeRateCharge);
        charge.description = formatStoneDetailsDescription({
          entityKeyDetails: productTypeRateCharge.entityKeyDetails,
          hideWeight: hideDiamondStoneWeight,
          hideRate: hideDiamondStoneRate
        });
        charge.rateWithoutDiscount = productTypeRateCharge.rateWithoutDiscount
          ? productTypeRateCharge.rateWithoutDiscount
          : '';
        charge.discount = formatStoneDiscount(productTypeRateCharge.entityKeyDetails);
        charge.priceDescription = productTypeRateCharge.formattedRateWithoutDiscount
          ? productTypeRateCharge.formattedRateWithoutDiscount
          : '';
        if (entityType === 'DIAMOND') {
          if (!hideStoneSummarySection) {
            diamondCharges.push(charge);
          }
          return;
        }
        if (!hideStoneSummarySection) {
          stoneCharges.push(charge);
        }
        return;
      }

      case ENTITY_BASE.WASTAGE_CHARGE: {
        charge.title = I18n.t('WASTAGE_CHARGE');
        charge.entityCalculation = productTypeRateCharge.entityCalculation;
        const { percentFigure, wastageOn = 'NET_WEIGHT' } =
          productTypeRateCharge.entityCalculation || {};
        const getWastageChargeDescription = () => {
          if (!percentFigure) {
            return undefined;
          }
          if (percentFigure) {
            return `(${
              I18n.t('PERCENT_OF_WASTAGE', {
                percent: percentFigure,
                wastageOn: I18n.t(wastageOn)
              }) || ''
            })`;
          }
          return '';
        };
        charge.description = getWastageChargeDescription();
        if (!hideWastageChargesSection) {
          wastageCharges.push(charge);
        }
        return;
      }

      case ENTITY_BASE.MAKING_CHARGE: {
        charge.entityCalculation = productTypeRateCharge.entityCalculation;
        let { percentFigure, perGramCost, grams } = productTypeRateCharge.entityCalculation || {};
        charge.title = I18n.t('MAKING_CHARGES');
        const getMakingChargeDescription = () => {
          if (hideMakingChargesWeight) {
            grams = undefined;
          }
          if (hideMakingChargesRate) {
            perGramCost = undefined;
          }
          if (percentFigure && (hideMakingChargesWeight || hideMakingChargesRate)) {
            grams = undefined;
            perGramCost = undefined;
            percentFigure = undefined;
          }
          if (!percentFigure && !grams && !perGramCost) {
            return undefined;
          }
          if (percentFigure) {
            return `(${percentFigure} ${I18n.t('OF_METAL_CHARGES')})`;
          }
          if (!grams && perGramCost) {
            return `${getFormattedPrice(perGramCost)}/${gm}`;
          }
          if (grams && !perGramCost) {
            return `${formatWeight(grams)}`;
          }
          return `${formatWeight(grams)} x ${getFormattedPrice(perGramCost)}/${gm}`;
        };
        charge.description = getMakingChargeDescription();
        if (!hideMakingChargesSection) {
          makingCharges.push(charge);
        }
        return;
      }

      default: {
        charge.title = entityType;
        otherCharges.push(charge);
      }
    }
  });

  return {
    metalCharges,
    diamondCharges,
    stoneCharges,
    otherCharges,
    makingCharges,
    wastageCharges
  };
};
